//* Third Party Libraries
import Downshift from "downshift";

//* Local or Custom Libraries
import { Select } from "@lululemon/ui-library";
import BopisModalStoreList from "./bopis-modal-store-list";
import BopisSearchQueryMessage from "./bopis-search-message-query";
import {
  INVENTORY_TYPE,
  USER_COUNTRY,
  locationsAboveMessage,
  noBopisStoresAvailableMessage,
  visitUsMessage
} from "../utilities/constants";

//* Importing Styles
import styles from "../bopis-modal.module.scss";

/**
 * @param {object} activeStoreRadioBtn - an object containing id and name of the current selected store.
 * @param {array} nearbyBopisStores - an array that contains all neaby stores.
 * @param {array} nearbyStoresWithInventoryButNoBopis - an array that contains all nearby stores with inventory but no BOPIS.
 * @param {function} setActiveStoreRadioBtn - the setter for the activeStoreRadioBtn state in the connector.
 * @param {function} setDeliveryToShip - a function that changes delivery type to SHP.
 * @param {function} searchQueryError - an error set from the geocodeAddress function if no address could be found.
 * @param {object} selectedStore - an object that contains id and name of selected store.
 * @param {string} userCountry - a string containing the country code.
 * @param {object} userLocation - an object that contains data of user location.
 * @param {object} userSearchedLocation - the userSearchedLocation state in `userNearbyStores`.
 * @param {object} userSearchedQuery - the given value that user typed in search bar.
 * @param {number} distanceFilter - a distance Filter Value.
 * @param {function} setDistanceFilter - a callback to Update the Distance Filter Value.
 */
const BopisModalStoreResults = ({
  activeStoreRadioBtn,
  nearbyBopisStores,
  nearbyStoresWithInventoryButNoBopis,
  setActiveStoreRadioBtn,
  setDeliveryToShip,
  searchQueryError,
  selectedStore,
  userCountry,
  userLocation,
  userSearchedLocation,
  userSearchedQuery,
  distanceFilter,
  setDistanceFilter
}) => {
  const displayLocation = () => {
    if (!userLocation) return;
    const detectedLocation = userLocation.address
      ? `${userLocation.address}`
      : `${userLocation.city}, ${userLocation.state}`;
    return userSearchedLocation?.address
      ? userSearchedLocation.address
      : detectedLocation;
  };

  const unitOfDistance = userCountry === USER_COUNTRY.US ? "miles" : "km";

  const distanceDropdownOptions = () => {
    return [
      { label: `5 ${unitOfDistance}`, value: "5" },
      { label: `10 ${unitOfDistance}`, value: "10" },
      { label: `25 ${unitOfDistance}`, value: "25" },
      { label: `50 ${unitOfDistance}`, value: "50" },
      { label: `75 ${unitOfDistance}`, value: "75" },
      { label: `100 ${unitOfDistance}`, value: "100" },
      { label: `125 ${unitOfDistance}`, value: "125" }
    ];
  };
  return (
    <div className={styles.resultsContainer}>
      <div className={styles.resultsTextAndSelect}>
        <p className={styles.storeResultsText}>
          {`${
            nearbyBopisStores.length
          } stores within ${distanceFilter} ${unitOfDistance} of ${displayLocation()}`}
        </p>
        <Select
          id="searchDistanceFilter"
          options={distanceDropdownOptions()}
          placeholder={`${distanceFilter} ${unitOfDistance}`}
          defaultValue={`${distanceFilter} ${unitOfDistance}`}
          onChange={(event) => {
            if (event.target.value) {
              setDistanceFilter(parseInt(event.target.value));
            }
          }}
          Downshift={Downshift}
        />
      </div>
      {nearbyBopisStores.length > 0 && (
        <BopisModalStoreList
          activeStoreRadioBtn={activeStoreRadioBtn}
          inventoryType={INVENTORY_TYPE.BOPIS}
          nearbyBopisStores={nearbyBopisStores}
          setActiveStoreRadioBtn={setActiveStoreRadioBtn}
          selectedStore={selectedStore}
          unitOfDistance={unitOfDistance}
        />
      )}
      {nearbyStoresWithInventoryButNoBopis?.length > 0 && (
        <>
          <hr className="divider" />
          {nearbyBopisStores.length === 0 ? (
            <p className={styles.noBopisStoresAvailableText}>
              {noBopisStoresAvailableMessage}
            </p>
          ) : (
            <div className={styles.noBopisInventoryTextContainer}>
              <p>{locationsAboveMessage}</p>
              <p>{visitUsMessage}</p>
            </div>
          )}
          <BopisModalStoreList
            nearbyBopisStores={nearbyStoresWithInventoryButNoBopis}
            unitOfDistance={unitOfDistance}
          />
        </>
      )}
      {nearbyBopisStores.length === 0 &&
        nearbyStoresWithInventoryButNoBopis?.length === 0 &&
        !searchQueryError && (
          <BopisSearchQueryMessage
            setDeliveryToShip={setDeliveryToShip}
            userCountry={userCountry}
            userSearchedQuery={userSearchedQuery}
          />
        )}
    </div>
  );
};

export default BopisModalStoreResults;
